<template>
    <rkm-modal
        ref="RkmModal"
        title="Procedimentos"
        extraLarge
    >

        <rkm-area
            title="Adicionar procedimento"
            type="fieldset"
            no-margin-top
        >

            <rkm-form ref="RkmForm">

                <rkm-row v-if="loaded">
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.usuario"
                            :requestQueryStringParams="autocompletesRequestParams.usuario"
                            :searchFields="fields.autocompletes.usuario"
                            :validations="validations.usuario"
                            text-field="cidadaoNome"
                            label="Profissional"
                            placeholder="Digite o nome ou número do conselho profissional"
                            v-model="usuario"
                            uppercase
                        />

                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            label="Código da equipe"
                            :options="equipes"
                            textField="descricao"
                            v-model="equipeId"
                            valueField="id"
                            show-default-option
                            :disabled="usuario && usuario.id ? false : true"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            label="Ocupação"
                            :options="ocupacoes"
                            textField="codigoNome"
                            v-model="ocupacaoId"
                            :validations="validations.ocupacao"
                            :disabled="usuario && usuario.id ? false : true"
                            valueField="id"
                            show-default-option
                        />
                    </rkm-col>

                    <rkm-col cols="8" class="pb-2">
                        <rkm-form-input-autocomplete
                            :repository="repositories.procedimento"
                            :request-query-string-params="autocompletesRequestParams.procedimento.queryString"
                            :search-fields="fields.autocompletes.procedimento"
                            :text-field="p => `${p.origem === 'abpg' ? p.codigo : formatProcedimento(p.codigo)} - ${p.nome}`"
                            name="Procedimento"
                            label="Procedimento"
                            v-model="procedimento"
                            :validations="validations.procedimento"
                            @input="handleProcedimento()"
                            :disabled="ocupacaoId ? false : true"
                        >

                            <template v-slot:itemLabel="{ item }">
                                {{ item.origem === 'abpg' ? item.codigo : formatProcedimento(item.codigo) }} - {{ item.nome }}
                            </template>

                            <rkm-input-group-append slot="append">
                                <rkm-input-group-text>
                                    <rkm-form-radio-group v-model="searchByApelido">
                                        <rkm-form-radio :value="false">Código / Nome</rkm-form-radio>
                                        <rkm-form-radio :value="true">Apelido</rkm-form-radio>
                                    </rkm-form-radio-group>
                                </rkm-input-group-text>
                            </rkm-input-group-append>
                        </rkm-form-input-autocomplete>
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :disabled="!procedimento || procedimento.origem === 'abpg'"
                            :validations="validations.quantidade"
                            label="Quantidade"
                            type="number"
                            v-model="quantidade"
                        />
                    </rkm-col>

                    <rkm-col class="flex-grow-1">
                        <rkm-form-input-autocomplete
                            :repository="repositories.cid"
                            :requestQueryStringParams="autocompletesRequestParams.cid"
                            :validations="validations.cid"
                            :searchFields="fields.cid"
                            :text-field="p => `${p.codigo} - ${p.nome}`"
                            :disabled="procedimento ? false : true"
                            label="CID"
                            v-rkm-tooltip.hover
                            v-model="cid"
                        >
                            <template v-slot:itemLabel="{ item }">
                                {{ formatCid(item.codigo) }} - {{ item.nome }}
                            </template>
                        </rkm-form-input-autocomplete>
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            color="primary"
                            @click="saveProcedimento()"
                            :label="false"
                        >
                            Incluir
                        </rkm-button>

                        <rkm-button
                            @click="resetForm"
                            :label="false"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            title="Listagem"
            no-margin-top
        >
            <rkm-table
                v-if="loaded"
                :fields="fields.procedimento"
                :items="model.procedimentos"
            >

                <template v-slot:cell(codigo)="row">
                    {{ row.item.codigoAbOrigem === 'abpg' ? row.item.codigo : formatProcedimento(row.item.codigo) }}
                </template>

                <template v-slot:cell(equipe)="row">
                    {{ row.item.equipe ?? '-' }}
                </template>

                <template v-slot:cell(cid)="row">
                    <span v-if="row.item.cid">
                        {{ row.item.cid.codigo | formatCid }} - {{ row.item.cid.nome }}
                    </span>
                    <span v-else> - </span>
                </template>

                <template v-slot:cell(actions)="{ index, item }">

                    <rkm-button
                        @click.stop="edit(item)"
                        color="primary"
                        small
                        title="Editar"
                    >
                        <rkm-icon icon="pen" />
                    </rkm-button>

                    <rkm-button
                        @click="destroy(index)"
                        color="danger"
                        small
                        title="Excluir"
                    >
                        <rkm-icon icon="trash" />
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-area>

        <template v-slot:footer="{cancel}">
            <rkm-button @click="cancel">
                Fechar
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>

// Mixins.
import { repository, object, formats, stringCase, data } from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

// Moment.
import moment from 'moment'

// Filters.
import format from '@/filters/formats'

export default {
    computed: {
        withCodigoAb() {

            const ocupacao = this.ocupacoes.find(ocupacao => ocupacao.id === this.ocupacaoId)

            return !ocupacao || !!ocupacao.permiteCodigoAb
        },
        validations() {
            return {
                usuario: { required: true },
                procedimento: { required: true },
                ocupacao: { required: true },
                quantidade: {
                    required: this.procedimento ? true : false,
                    min_value: 1,
                    ...(this.procedimento && this.procedimento.quantidadeMaximaExecucao
                        ? { max_value: this.procedimento.quantidadeMaximaExecucao }
                        : {})
                },
                cid: { required: this.procedimento && this.procedimento.hasCidPrincipal ? true : false }
            }
        },
        autocompletesRequestParams() {

            return {
                usuario: {
                    with_not_regularized: true,
                },
                procedimento: {
                    queryString: {
                        search_by_apelido: this.searchByApelido,
                        paciente_id: this.model ? this.model.pacienteId : null,
                        ocupacao_id: this.ocupacaoId,
                        competencia: this.competencia,
                        with_codigo_ab: this.withCodigoAb,
                        with_abex: this.withCodigoAb,
                    }
                },
                cid: {
                    procedimento_id: this.procedimento && this.procedimento.hasCidPrincipal
                        ? this.procedimento.id
                        : null
                }
            }
        },
        competencia() {

            if (this.model && this.model.data) {

                return moment(this.model.data).format('YYYYMM')
            }

            return ''
        },
        fields() {

            return {
                procedimento: [
                    {
                        key: "usuario.cidadaoNome",
                        label: "Profissional",
                    },
                    {
                        key: "equipe",
                        label: "Equipe",
                    },
                    {
                        key: "ocupacao",
                        label: "Ocupação",
                    },
                    {
                        key: "codigo",
                        label: "Código",
                        class: "col-min",
                    },
                    {
                        key: "nome",
                        label: "Procedimento",
                    },
                    {
                        key: "quantidade",
                        label: "Quantidade",
                    },
                    {
                        key: "cid",
                        label: "CID",
                    },
                    {
                        key: "actions",
                        label: "Ações",
                        class: "col-min",
                    },
                ],
                autocompletes: {
                    procedimento: this.searchByApelido
                        ? [{ key: 'parametrizacao_procedimento.apelido', searchable: true }]
                        : [{ key: 'nome', searchable: true }, { key: 'codigo', searchable: true }],
                    usuario: [
                        { key: "cadastroCidadao.nome", searchable: true },
                        { key: "cadastroUsuario.registroNumero", searchable: true },
                    ],
                },
                cid: [
                    { key: 'codigo', searchable: true },
                    { key: 'nome', searchable: true },
                ],
            }
        },
    },
    created() {
        this.repositories = {
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            fechamento: this.getConectaSusRepository('ambulatorio.fechamentoConsulta'),
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }
    },
    data: () => ({
        id: null,
        loaded: false,
        model: null,
        cid: null,
        procedimento: null,
        quantidade: null,
        usuario: null,
        unidade: null,
        infraestrutura: null,
        ocupacaoId: null,
        equipeId: null,
        searchByApelido: false,
        procedimentoAtendimento: null,
        equipes: [],
        ocupacoes: [],
        index: null
    }),
    filters: {
        ...format,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(item) {

            this.index = null

            this.model = item
            this.loaded = true
            this.unidade = item.unidade
            this.infraestrutura = item.infraestrutura
            this.usuario = item.cadastro.usuario
            this.procedimentoAtendimento = item.procedimentosAtendimento
            this.procedimento = item.procedimentoAtendimento
            this.quantidade = item.quantidade

            this.$refs.RkmModal.show()
        },
        getEquipes() {

            const request = {
                usuarioId: this.usuario.id,
                infraestruturaId: this.model.infraestruturaId,
                pacienteId: this.model.pacienteId,
                data: this.model.data,
            }

            this.setLoading()

            this.repositories.fechamento.getEquipes(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.equipes = Object.assign(response.data)

                    if (response.data.length === 1){

                        this.equipeId = response.data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getOcupacoes() {

            this.setLoading()

            const request = {
                usuarioId: this.usuario.id,
                infraestruturaId: this.model.infraestruturaId,
                pacienteId: this.model.pacienteId,
                data: this.model.data,
            }

            this.repositories.fechamento.getOcupacoes(this.camelObjectKeysToSnakeRecursive(request))
                .then(({ data }) => {

                    this.ocupacoes = data
                    if (data.length === 1){

                        this.ocupacaoId = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        saveProcedimento() {
            this.$refs.RkmForm.validate()
                .then(valid => {

                    if (!valid) return

                    const procedimento = this.model.procedimentos
                        .find(procedimento => procedimento.id == this.procedimento.id && procedimento.usuarioId == this.usuario.id)

                    const procedimentoIndex = this.model.procedimentos
                        .findIndex(procedimento => procedimento.id == this.procedimento.id && procedimento.usuarioId == this.usuario.id)

                    const procedimentoAtendimento = this.procedimentoAtendimento
                        .some(p => p.codigo == this.procedimento.codigo && p.usuarioId == this.usuario.id)

                    if (this.model.procedimento && this.model.procedimento.id == this.procedimento.id) {

                        this.showAlert(`Este procedimento já está cadastrado como procedimento padrão, portanto não é possível
                            cadastrá-lo novamente.`, "Atenção")
                        return false
                    }

                    if (procedimento && procedimentoIndex !== this.index || procedimentoAtendimento){
                        this.showAlert('O procedimento já foi cadastrado anteriormente para este profissional nesta ficha.', "Atenção")
                        return false
                    }

                    if (!this.ocupacaoId) {
                        this.showAlert('A ocupação do profissional não está sendo listada devido à ausência de jornada definida.', "Atenção")
                        return false
                    }

                    const equipe = this.equipeId && this.equipes.length > 0 ? this.equipes.find(e => e.id === this.equipeId).descricao : null
                    const ocupacao = this.ocupacoes.find(e => e.id === this.ocupacaoId).codigoNome

                    const param = {
                        id: this.procedimento.id,
                        nome: this.procedimento.nome,
                        codigo: this.procedimento.codigo,
                        quantidadeMaximaExecucao: this.procedimento.quantidadeMaximaExecucao,
                        hasInstrumentoRegistroRaas: this.procedimento.hasInstrumentoRegistroRaas,
                        usuario: this.usuario,
                        usuarioId: this.usuario.id,
                        equipeId: this.equipeId,
                        ocupacaoId: this.ocupacaoId,
                        equipe: equipe,
                        ocupacao: ocupacao,
                        origem: 'ambulatorio',
                        codigoAbOrigem: this.procedimento.origem,
                        quantidade: this.quantidade,
                        cid: this.cid,
                        padrao: false,
                    }

                    if (this.index == null) {

                        this.model.procedimentos.push(param)
                    } else {
                        this.$set(this.model.procedimentos, this.index, this.clone(param))
                    }

                    this.$emit('proc', this.model.procedimentos)
                    this.resetProcedimento()
                })
        },
        resetProcedimento() {

            this.procedimento = null
            this.quantidade = null
            this.cid = null
            this.index = null

            this.$refs.RkmForm.resetValidator()
        },
        destroy(index) {

            const destroy = async () => this.model.procedimentos.splice(index, 1)

            this.showDeleteConfirmation('Deseja realmente excluir o procedimento?', destroy)
        },
        edit(item) {

            this.index = this.model.procedimentos.findIndex(procedimento => procedimento.id == item.id
                && procedimento.usuario == item.usuario
                && procedimento.ocupacaoId == item.ocupacaoId)

            const itemClone = this.clone(item)

            this.usuario = itemClone.usuario

            this.$nextTick().then(() => {

                    this.equipeId = itemClone.equipeId
                    this.ocupacaoId = itemClone.ocupacaoId
                    this.procedimento = {
                        id: itemClone.id,
                        codigo: itemClone.codigo,
                        nome: itemClone.nome,
                        quantidadeMaximaExecucao: itemClone.quantidadeMaximaExecucao,
                        hasInstrumentoRegistroRaas: itemClone.hasInstrumentoRegistroRaas,
                        origem: itemClone.codigoAbOrigem,
                    }
                    this.quantidade = itemClone.quantidade
                    this.cid = itemClone.cid
            })
        },
        resetForm() {

            this.ocupacoes = []
            this.equipes = []

            this.usuario = null
            this.ocupacaoId = null
            this.equipeId = null
            this.procedimento = null
            this.quantidade = null
            this.cid = null
            this.index = null

            this.$refs.RkmForm.resetValidator()
        },
        handleProcedimento() {

            if (this.procedimento && this.procedimento.id) {

                this.quantidade = 1
                this.cid = null
            } else {

                this.quantidade = null
                this.cid = null
            }
        },
    },
    mixins: [ repository, object, formats, stringCase, data ],
    watch: {
        'usuario': function (usuario) {
            if (usuario) {

                this.ocupacoes = []
                this.equipes = []

                this.ocupacaoId = null
                this.equipeId = null
                this.procedimento = null
                this.quantidade = null
                this.cid = null

                this.getEquipes()
                this.getOcupacoes()
            } else {

                this.ocupacoes = []
                this.equipes = []

                this.ocupacaoId = null
                this.equipeId = null
                this.procedimento = null
                this.quantidade = null
                this.cid = null
            }
        }
    }
}
</script>